import React, { useEffect, useState, useRef, useMemo } from "react";
import FormTemplate from "../../../forms/FormTemplate";
import {
  Box,
  Button,
  Stack,
  Typography,
  Select,
  MenuItem,
  InputLabel,
  lighten,
  Radio,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import "../../../styles/SMSSideMenu.css";
import useAutomations from "../../../hooks/automationhooks/useAutomations";
import SMSTemplateSelection from "./SMSTemplateSelection";
import { useParams } from "react-router-dom";
import SelectArrow from "../../../styles/icons/SelectArrow";
import ROUTES from "../../../routerfolder/Routes";
import SMSCreateSenderMenu from "./SMSCreateSenderMenu";

export default function SMSSideMenu({
  onClose,
  onSubmit,
  selectedNode,
  preSelectedTemplate = null,
}) {
  const { brandId } = useParams();
  const {
    isLoading,
    smsTemplates,
    smsSenders,
    handleGetSmsSenders,
    handleGetSmsTemplates,
    handleCreateSmsSender,
  } = useAutomations();

  /* ---------------- local state ---------------- */
  const [tplSelOpen, setTplSelOpen] = useState(false);
  const [senderOpen, setSenderOpen] = useState(false);

  const [selectedTpl, setSelectedTpl] = useState(null);
  const [messageText, setMessageText] = useState("");
  const [templateParams, setTemplateParams] = useState([]);
  const [placeholderValues, setPlaceholderValues] = useState({});
  const [selectedSender, setSelectedSender] = useState("");

  // send timing
  const [sendTiming, setSendTiming] = useState("immediate"); // 'immediate' | 'scheduled'
  const [timeToSend, setTimeToSend] = useState(null); // string with offset: YYYY-MM-DDTHH:mm:ssZ

  /* guard so we only pre‑handle once */
  const preHandled = useRef(false);

  /* ------------ initial data fetch ------------- */
  useEffect(() => {
    handleGetSmsSenders(brandId);
    handleGetSmsTemplates(brandId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ------------- hydrate when editing existing node ------------- */
  useEffect(() => {
    if (!selectedNode) return;
    if (!smsTemplates?.data?.length) return;

    const { template, sendFrom } = selectedNode.data ?? {};

    if (template?.value) {
      if (!selectedTpl || selectedTpl.Id !== template.value) {
        const match = smsTemplates.data.find((t) => t.Id === template.value);
        if (match) {
          applyTemplate(match, template.placeholderMappings || {});
        }
      }
    }

    if (sendFrom?.value && selectedSender === "") {
      setSelectedSender(sendFrom.value);
    }

    // hydrate scheduling
    const existingTiming = selectedNode.data?.sendTiming?.value;
    const existingTimeToSend = selectedNode.data?.timeToSend;
    if (
      existingTiming &&
      (existingTiming === "immediate" || existingTiming === "scheduled")
    ) {
      setSendTiming(existingTiming);
    } else if (existingTimeToSend) {
      // backward/partial compatibility: if timeToSend exists, treat as scheduled
      setSendTiming("scheduled");
    }
    if (existingTimeToSend && !timeToSend) {
      setTimeToSend(existingTimeToSend);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNode, smsTemplates?.data]);

  /* ---------------- creation pre‑selection ---------------- */
  useEffect(() => {
    if (preSelectedTemplate && !selectedNode && !preHandled.current) {
      applyTemplate(preSelectedTemplate);
      preHandled.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preSelectedTemplate, selectedNode]);

  /* ------------- derived validity ------------- */
  const isValid = useMemo(() => {
    const filled = templateParams.every(
      (k) => placeholderValues[k] && placeholderValues[k] !== "null",
    );
    const hasSchedule = sendTiming !== "scheduled" || Boolean(timeToSend);
    return (
      Boolean(selectedTpl) && filled && Boolean(selectedSender) && hasSchedule
    );
  }, [
    selectedTpl,
    placeholderValues,
    templateParams,
    selectedSender,
    sendTiming,
    timeToSend,
  ]);

  /* ================= helpers ================= */
  const applyTemplate = (tpl, savedMappings = {}) => {
    const params =
      tpl.TemplateParameters ??
      Array.from(new Set((tpl.Content || "").match(/(\[#.*?#\])/g) || []));

    const initMap = {};
    params.forEach((p) => {
      initMap[p] = savedMappings[p] ?? "null";
    });

    setSelectedTpl(tpl);
    setMessageText(tpl.Content);
    setTemplateParams(params);
    setPlaceholderValues(initMap);
  };

  const handleTemplateChosen = (tpl) => {
    if (!selectedTpl || selectedTpl.Id !== tpl.Id) {
      applyTemplate(tpl);
    }
    setTplSelOpen(false);
  };

  const handlePlaceholderChange = (ph, val) => {
    setPlaceholderValues((prev) => ({ ...prev, [ph]: val }));
  };

  const handleNewSender = async (brand, senderName) => {
    await handleCreateSmsSender(brand, senderName);
    console.log("await ended");
    console.log(brand);
    console.log(senderName);

    handleGetSmsSenders(brand);
  };

  const handleSave = () => {
    if (!isValid) return;

    // Data payload used when creating a NEW node
    const dataPayload = {
      label: "SMS Message",
      sendFrom: { value: selectedSender, label: selectedSender },
      sendTiming: {
        value: sendTiming,
        label: sendTiming === "scheduled" ? "Scheduled" : "Immediate",
      },
      timeToSend: sendTiming === "scheduled" ? timeToSend : null,
      template: {
        value: selectedTpl.Id,
        label: selectedTpl.TemplateName,
        placeholderMappings: { ...placeholderValues },
      },
    };

    if (selectedNode) {
      // --- EDIT FLOW --------------------------------------------------
      // Mutate the existing node's data (same pattern used originally)
      selectedNode.data.sendFrom = {
        value: selectedSender,
        label: selectedSender,
      };
      selectedNode.data.sendTiming = {
        value: sendTiming,
        label: sendTiming === "scheduled" ? "Scheduled" : "Immediate",
      };
      selectedNode.data.timeToSend =
        sendTiming === "scheduled" ? timeToSend : null;
      selectedNode.data.template.value = selectedTpl.Id;
      selectedNode.data.template.label = selectedTpl.TemplateName;
      selectedNode.data.template.placeholderMappings = { ...placeholderValues };

      // let NodeSideMenu know which template was chosen so it can update
      onSubmit(selectedTpl);
    } else {
      // --- CREATION FLOW ---------------------------------------------
      // Pass full data for addNodeBetweenNodes
      onSubmit(dataPayload);
    }

    onClose();
  };

  /* -------- render placeholder‑rich text -------- */
  const renderRichMessage = (txt) =>
    txt.split(/(\[#.*?#\])/g).map((part, i) =>
      /(\[#.*?#\])/.test(part) ? (
        <Select
          key={i}
          size="small"
          value={placeholderValues[part] || "null"}
          onChange={(e) => handlePlaceholderChange(part, e.target.value)}
          IconComponent={SelectArrow}
          sx={{ m: "0 4px", fontSize: "0.875rem", borderRadius: 4 }}
        >
          <MenuItem value="null" disabled>
            Select
          </MenuItem>
          {["FirstName", "LastName", "FullName", "Id", "Phone", "QrCode"].map(
            (opt) => (
              <MenuItem key={opt} value={opt}>
                {opt === "QrCode" ? "QR Code" : opt}
              </MenuItem>
            ),
          )}
        </Select>
      ) : (
        <span key={i}>{part}</span>
      ),
    );

  if (isLoading)
    return <Typography sx={{ p: 2 }}>Loading templates…</Typography>;

  /* ================= JSX ================= */
  return (
    <>
      <FormTemplate
        title={selectedNode ? "Edit SMS" : "Create SMS"}
        isValid={isValid}
        handleSubmit={handleSave}
        onClose={onClose}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* top buttons */}
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              color="companyBlue"
              sx={{
                borderRadius: "16px", // Change the border radius here
                overflow: "visible", // Prevent the rounded corners from being clipped
                textTransform: "none",
                boxShadow: 0,
                color: "white",
                "&:hover": {
                  backgroundColor: (theme) =>
                    lighten(theme.palette.companyBlue.main, 0.1), // Lightens the color by 20%
                },
              }}
              onClick={() => setTplSelOpen(true)}
            >
              Select Template
            </Button>
            <Button
              variant="outlined"
              sx={{
                borderRadius: "16px", // Change the border radius here
                overflow: "visible", // Prevent the rounded corners from being clipped
                textTransform: "none",
                color: "white",
                boxShadow: 0,
              }}
              onClick={() => setSenderOpen(true)}
            >
              Create Sender
            </Button>
          </Stack>

          {/* template preview */}
          {selectedTpl ? (
            <Box>
              <Typography>Selected Template:</Typography>
              <Box
                sx={{
                  border: 1,
                  borderRadius: 1,
                  p: 2,
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "center",
                }}
              >
                <Typography variant="body2">
                  {renderRichMessage(messageText)}
                </Typography>
              </Box>
            </Box>
          ) : (
            <Typography>No Template Selected</Typography>
          )}

          {/* sender picker */}
          <Stack
            direction="row"
            spacing={2}
            sx={{ alignItems: "flex-start", flexWrap: "wrap" }}
          >
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <InputLabel sx={{ mt: 2 }}>Select Sender</InputLabel>
              <Select
                value={selectedSender}
                onChange={(e) => setSelectedSender(e.target.value)}
                IconComponent={SelectArrow}
                sx={{ minWidth: 240, borderRadius: 4 }}
              >
                {smsSenders.data && smsSenders.data.length ? (
                  smsSenders.data.map((s) => (
                    <MenuItem key={s.SenderName} value={s.SenderName}>
                      {s.SenderName}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>No senders found</MenuItem>
                )}
              </Select>
            </Box>

            <Box
              sx={{ display: "flex", flexDirection: "column", minWidth: 520 }}
            >
              <Typography
                sx={{ mt: 2, mb: 0.5, fontSize: 14, fontWeight: 500 }}
              >
                When should it be sent
              </Typography>

              <Stack
                direction="row"
                spacing={2}
                sx={{
                  alignItems: "center",
                  flexWrap: "wrap",
                  border: "1px solid rgba(255,255,255,0.12)",
                  borderRadius: 2,
                  px: 2,
                  py: 1.25,
                }}
              >
                {/* Left: Immediate option */}
                <FormControlLabel
                  value="immediate"
                  control={
                    <Radio
                      size="small"
                      sx={{
                        color: (theme) => theme.palette.companyBlue.main,
                        "&.Mui-checked": {
                          color: (theme) => theme.palette.companyBlue.main,
                        },
                      }}
                      checked={sendTiming === "immediate"}
                      onChange={() => {
                        setSendTiming("immediate");
                        setTimeToSend(null);
                      }}
                    />
                  }
                  label="Immediate"
                  sx={{ m: 0 }}
                />

                {/* Right: Scheduled radio + date input */}
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ ml: "auto", alignItems: "center" }}
                >
                  <Radio
                    size="small"
                    sx={{
                      color: (theme) => theme.palette.companyBlue.main,
                      "&.Mui-checked": {
                        color: (theme) => theme.palette.companyBlue.main,
                      },
                    }}
                    checked={sendTiming === "scheduled"}
                    onChange={() => setSendTiming("scheduled")}
                  />
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateTimePicker
                      value={timeToSend ? dayjs(timeToSend) : null}
                      onChange={(newVal) => {
                        setTimeToSend(
                          newVal ? newVal.format("YYYY-MM-DDTHH:mm:ssZ") : null,
                        );
                      }}
                      disabled={sendTiming !== "scheduled"}
                      disablePast
                      ampm={false}
                      format="DD/MM/YYYY HH:mm"
                      slotProps={{
                        textField: {
                          size: "small",
                          placeholder: "Set date and time",
                          error: sendTiming === "scheduled" && !timeToSend,
                          sx: {
                            minWidth: 260,
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          },
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Stack>
              </Stack>

              {/* Reserve space so the sender field doesn't "jump" when error appears */}
              <Typography
                variant="caption"
                color="error"
                sx={{ mt: 0.75, minHeight: 18 }}
              >
                {sendTiming === "scheduled" && !timeToSend
                  ? "Please pick a date & time"
                  : "\u00A0"}
              </Typography>
            </Box>
          </Stack>
        </Box>
      </FormTemplate>

      {/* template selection dialog */}
      <SMSTemplateSelection
        open={tplSelOpen}
        setSelected={handleTemplateChosen}
        setClosed={() => setTplSelOpen(false)}
        templates={smsTemplates?.data || []}
        refreshTemplates={() => handleGetSmsTemplates(brandId)}
      />

      {/* create sender dialog */}
      <SMSCreateSenderMenu
        open={senderOpen}
        setOpen={setSenderOpen}
        createNewSender={handleNewSender}
        brand={brandId}
      />
    </>
  );
}
